import React, { useState, useMemo, useEffect, useRef } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import {
  ChevronLeft,
  Search,
  Layers,
  Table as TableIcon,
  Download,
  ExternalLink,
  MapPin,
  X,
  Filter,
  RefreshCw,
  Cpu,
  Map as MapIcon,
  Maximize2,
  Building2,
  Activity,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import {
  NATIONWIDE_SUBSTATIONS,
  GRID_POWERLINES,
  TARGET_INDUSTRIAL_PARKS,
  type SubstationNode,
  type GridPowerline,
  type TargetIndustrialPark
} from '../services/adminGridDb';

interface AdminGridMapPageProps {
  onBackToHome: () => void;
  onBackToPortal?: () => void;
  onNavigateSubpage?: (page: 'admin-portal' | 'admin-proposal' | 'admin-diagnostic' | 'admin-golden-targets' | 'admin-map') => void;
}

export const AdminGridMapPage: React.FC<AdminGridMapPageProps> = ({
  onBackToHome,
  onBackToPortal,
  onNavigateSubpage
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedStatus, setSelectedStatus] = useState<string>('ALL');
  const [selectedRegion, setSelectedRegion] = useState<string>('ALL');
  const [selectedCity, setSelectedCity] = useState<string>('ALL');
  const [viewMode, setViewMode] = useState<'map' | 'table'>('map');
  const [tileMode, setTileMode] = useState<'street' | 'satellite' | 'dark'>('street');
  const [showPowerlines, setShowPowerlines] = useState<boolean>(true);
  const [showIndustrialParks, setShowIndustrialParks] = useState<boolean>(true);
  const [legendOpen, setLegendOpen] = useState<boolean>(false);
  const [currentZoom, setCurrentZoom] = useState<number>(7.5);

  // Selected State
  const [activeSubstation, setActiveSubstation] = useState<SubstationNode | null>(NATIONWIDE_SUBSTATIONS[0]);
  const [activePark, setActivePark] = useState<TargetIndustrialPark | null>(null);
  const [activeLine, setActiveLine] = useState<GridPowerline | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Map DOM & Leaflet Instance References
  const mapContainerRef = useRef<HTMLDivElement | null>(null);
  const mapInstanceRef = useRef<L.Map | null>(null);
  const markersLayerRef = useRef<L.LayerGroup | null>(null);
  const powerlinesLayerRef = useRef<L.LayerGroup | null>(null);
  const parksLayerRef = useRef<L.LayerGroup | null>(null);
  const tileLayerRef = useRef<L.TileLayer | null>(null);

  // Dynamic Cities list based on selected Region
  const availableCities = useMemo(() => {
    if (selectedRegion === 'ALL') {
      return Array.from(new Set(NATIONWIDE_SUBSTATIONS.map((s) => s.city)));
    }
    return Array.from(
      new Set(
        NATIONWIDE_SUBSTATIONS.filter((s) => s.region === selectedRegion).map((s) => s.city)
      )
    );
  }, [selectedRegion]);

  // Filter logic (Multi-tier: Status + Region + City + Search)
  const filteredSubstations = useMemo(() => {
    return NATIONWIDE_SUBSTATIONS.filter((sub) => {
      // 1. Status Filter
      if (selectedStatus !== 'ALL' && sub.status !== selectedStatus) {
        return false;
      }
      // 2. Region Filter
      if (selectedRegion !== 'ALL' && sub.region !== selectedRegion) {
        return false;
      }
      // 3. City Granular Filter
      if (selectedCity !== 'ALL' && sub.city !== selectedCity) {
        return false;
      }
      // 4. Search Query
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        const matchesName = sub.name.toLowerCase().includes(q);
        const matchesCity = sub.city.toLowerCase().includes(q);
        const matchesTown = sub.town.toLowerCase().includes(q);
        const matchesLocation = sub.locationName.toLowerCase().includes(q);
        const matchesBranch = sub.branchName.toLowerCase().includes(q);
        const matchesDl = sub.dlList.some((dl) => dl.name.toLowerCase().includes(q));
        return matchesName || matchesCity || matchesTown || matchesLocation || matchesBranch || matchesDl;
      }
      return true;
    });
  }, [selectedStatus, selectedRegion, selectedCity, searchQuery]);

  // KPI Metrics
  const metrics = useMemo(() => {
    const total = NATIONWIDE_SUBSTATIONS.length;
    const abundant = NATIONWIDE_SUBSTATIONS.filter((s) => s.status === 'ABUNDANT').length;
    const normal = NATIONWIDE_SUBSTATIONS.filter((s) => s.status === 'NORMAL').length;
    const saturated = NATIONWIDE_SUBSTATIONS.filter((s) => s.status === 'SATURATED').length;
    const totalRemainingKw = NATIONWIDE_SUBSTATIONS.reduce((acc, s) => acc + s.totalDlRemainKw, 0);
    const totalParks = TARGET_INDUSTRIAL_PARKS.length;
    const totalRoofPyung = TARGET_INDUSTRIAL_PARKS.reduce((acc, p) => acc + p.estimatedRoofPyung, 0);

    return { total, abundant, normal, saturated, totalRemainingKw, totalParks, totalRoofPyung };
  }, []);

  // Initialize Real Leaflet GIS Map Instance (Bulletproof Lifecycle)
  useEffect(() => {
    if (viewMode !== 'map' || !mapContainerRef.current) return;

    if (!mapInstanceRef.current) {
      const container = mapContainerRef.current;
      if ((container as unknown as { _leaflet_id?: string })._leaflet_id) {
        delete (container as unknown as { _leaflet_id?: string })._leaflet_id;
      }

      // Create Leaflet Map centered on South Korea
      const map = L.map(container, {
        center: [36.6, 127.8],
        zoom: 7.5,
        zoomControl: true,
        attributionControl: false,
      });

      mapInstanceRef.current = map;
      powerlinesLayerRef.current = L.layerGroup().addTo(map);
      parksLayerRef.current = L.layerGroup().addTo(map);
      markersLayerRef.current = L.layerGroup().addTo(map);

      // Track zoom events for adaptive decluttering
      map.on('zoomend', () => {
        setCurrentZoom(map.getZoom());
      });
    }

    const map = mapInstanceRef.current;

    // Update Tile Layer
    if (tileLayerRef.current) {
      map.removeLayer(tileLayerRef.current);
    }

    // Google Maps High-Speed Korean Tiles (Fastest & 100% Reliable anywhere in KR)
    let tileUrl = 'https://mt1.google.com/vt/lyrs=m&hl=ko&x={x}&y={y}&z={z}';
    let maxZoom = 19;
    if (tileMode === 'satellite') {
      tileUrl = 'https://mt1.google.com/vt/lyrs=y&hl=ko&x={x}&y={y}&z={z}';
      maxZoom = 18;
    } else if (tileMode === 'dark') {
      tileUrl = 'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png';
    }

    const newTileLayer = L.tileLayer(tileUrl, {
      maxZoom,
      subdomains: ['a', 'b', 'c', 'd'],
    }).addTo(map);

    tileLayerRef.current = newTileLayer;

    // Immediate & delayed size invalidation for zero layout delay
    map.invalidateSize();
    const timer = setTimeout(() => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.invalidateSize();
      }
    }, 250);

    return () => {
      clearTimeout(timer);
    };

  }, [viewMode, tileMode]);

  // 1. Render 22.9kV 배전선로망 (Grid Powerlines Polyline)
  useEffect(() => {
    if (viewMode !== 'map' || !mapInstanceRef.current || !powerlinesLayerRef.current) return;

    const powerlinesLayer = powerlinesLayerRef.current;
    powerlinesLayer.clearLayers();

    if (!showPowerlines) return;

    GRID_POWERLINES.forEach((line) => {
      const isSaturated = line.status === 'SATURATED';
      const lineColor = isSaturated ? '#ef4444' : '#10b981';
      const dashArray = isSaturated ? '6, 6' : undefined;

      const polyline = L.polyline(line.path, {
        color: lineColor,
        weight: currentZoom >= 10 ? 4.5 : 3,
        opacity: currentZoom >= 9 ? 0.85 : 0.6,
        dashArray,
      });

      polyline.on('click', () => {
        setActiveLine(line);
        const parentSub = NATIONWIDE_SUBSTATIONS.find((s) => s.id === line.substationId);
        if (parentSub) setActiveSubstation(parentSub);
      });

      polyline.bindTooltip(
        `<div style="font-size: 11px; font-weight: bold; font-family: sans-serif;">
          <span style="color: ${lineColor};">⚡ ${line.feederName}</span> (${line.voltage})<br/>
          잔여: ${isSaturated ? '0kW (포화)' : line.remainingKw.toLocaleString() + 'kW'}
        </div>`,
        { sticky: true }
      );

      polyline.addTo(powerlinesLayer);
    });
  }, [showPowerlines, currentZoom, viewMode]);

  // 2. Render Target Industrial Parks & Factory Clusters (Zoom-Adaptive)
  useEffect(() => {
    if (viewMode !== 'map' || !mapInstanceRef.current || !parksLayerRef.current) return;

    const parksLayer = parksLayerRef.current;
    parksLayer.clearLayers();

    if (!showIndustrialParks) return;

    const isHighZoom = currentZoom >= 10;

    TARGET_INDUSTRIAL_PARKS.forEach((park) => {
      const isSelected = activePark?.id === park.id;
      const isSaturated = park.status === 'SATURATED';
      const badgeBg = isSaturated ? 'bg-rose-500 border-rose-400' : 'bg-emerald-500 border-emerald-400';

      let html = '';
      let iconSize: [number, number] = [24, 24];
      let iconAnchor: [number, number] = [12, 12];

      if (isHighZoom) {
        // High zoom: Full detailed badge
        html = `
          <div class="relative flex items-center group cursor-pointer" style="transform: translate(-50%, -50%);">
            <div class="px-2 py-1 rounded-xl text-[10px] font-black text-white ${badgeBg} border shadow-lg flex items-center gap-1 whitespace-nowrap ${
              isSelected ? 'ring-4 ring-amber-400 scale-110' : ''
            }">
              <span>🏢</span>
              <span>${park.name}</span>
              <span class="bg-black/30 px-1 rounded text-[9px] font-bold">${(park.estimatedRoofPyung / 10000).toFixed(1)}만평</span>
            </div>
          </div>
        `;
        iconSize = [160, 28];
        iconAnchor = [80, 14];
      } else {
        // Low/Medium zoom: Compact Clean Circular Badge (No text overlap)
        html = `
          <div class="relative flex items-center justify-center cursor-pointer" style="transform: translate(-50%, -50%);">
            <div class="w-6 h-6 rounded-full bg-amber-500 border-2 border-white text-white shadow-lg flex items-center justify-center text-[10px] ${
              isSelected ? 'ring-4 ring-amber-400 scale-125' : 'hover:scale-110'
            } transition-transform">
              <span>🏢</span>
            </div>
          </div>
        `;
      }

      const customIcon = L.divIcon({
        className: 'custom-park-pin',
        html,
        iconSize,
        iconAnchor,
      });

      const marker = L.marker([park.lat, park.lng], { icon: customIcon, zIndexOffset: 50 });

      marker.on('click', () => {
        setActivePark(park);
        const parentSub = NATIONWIDE_SUBSTATIONS.find((s) => s.id === park.substationId);
        if (parentSub) setActiveSubstation(parentSub);
        if (mapInstanceRef.current) {
          mapInstanceRef.current.flyTo([park.lat, park.lng], 13, { duration: 0.8 });
        }
      });

      marker.bindTooltip(`
        <div style="font-family: sans-serif; font-size: 11px; color: #0f172a; line-height: 1.4;">
          <strong>🏢 ${park.name}</strong><br/>
          <span>지붕: ${(park.estimatedRoofPyung / 10000).toFixed(1)}만 평 (${(park.potentialKw / 1000).toFixed(1)}MW)</span><br/>
          <span>연계선로: ${park.connectedFeeder}</span>
        </div>
      `);

      marker.addTo(parksLayer);
    });
  }, [showIndustrialParks, activePark, currentZoom, viewMode]);

  // 3. Render Real Geo-Pins on Leaflet Map (Smart Zoom-Adaptive Engine)
  useEffect(() => {
    if (viewMode !== 'map' || !mapInstanceRef.current || !markersLayerRef.current) return;

    const markersLayer = markersLayerRef.current;
    markersLayer.clearLayers();

    const isHighZoom = currentZoom >= 9.5;

    filteredSubstations.forEach((sub) => {
      const isSelected = activeSubstation?.id === sub.id;

      let colorClass = 'bg-emerald-500 border-emerald-300 text-white shadow-emerald-500/50';
      let pingClass = 'bg-emerald-400';
      let dotBg = 'bg-emerald-500';
      if (sub.status === 'SATURATED') {
        colorClass = 'bg-rose-500 border-rose-300 text-white shadow-rose-500/50';
        pingClass = 'bg-rose-400';
        dotBg = 'bg-rose-500';
      } else if (sub.status === 'NORMAL') {
        colorClass = 'bg-amber-500 border-amber-300 text-white shadow-amber-500/50';
        pingClass = 'bg-amber-400';
        dotBg = 'bg-amber-500';
      }

      const shortTownName = sub.town.replace('읍', '').replace('면', '').replace('동', '');
      const remainText = sub.status === 'SATURATED' ? '0kW' : `${(sub.totalDlRemainKw / 1000).toFixed(1)}M`;

      let html = '';
      let iconSize: [number, number] = [28, 28];
      let iconAnchor: [number, number] = [14, 14];

      if (isHighZoom) {
        // Zoom >= 9.5: Expanded Informative Badge
        html = `
          <div class="relative flex items-center group cursor-pointer" style="transform: translate(-50%, -50%);">
            <span class="absolute -inset-1 rounded-full ${pingClass} opacity-60 animate-ping"></span>
            <div class="relative flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-black shadow-xl border-2 ${colorClass} ${
              isSelected ? 'ring-4 ring-sky-400 scale-110' : 'hover:scale-105'
            } whitespace-nowrap transition-transform">
              <span class="w-2 h-2 rounded-full bg-white animate-pulse"></span>
              <span>${sub.city} ${sub.town}</span>
              <span class="text-[9px] bg-black/30 px-1 rounded font-bold">${remainText}</span>
            </div>
          </div>
        `;
        iconSize = [130, 32];
        iconAnchor = [65, 16];
      } else {
        // Zoom < 9.5: Compact Decluttered Dot + Town Label (100% Overlap-Free!)
        html = `
          <div class="relative flex flex-col items-center group cursor-pointer" style="transform: translate(-50%, -50%);">
            <span class="absolute -inset-1 rounded-full ${pingClass} opacity-50 animate-ping"></span>
            <div class="w-5 h-5 rounded-full ${dotBg} border-2 border-white shadow-md flex items-center justify-center text-[9px] font-black text-white ${
              isSelected ? 'ring-4 ring-sky-400 scale-125' : 'group-hover:scale-125'
            } transition-transform">
              <span class="w-1.5 h-1.5 rounded-full bg-white"></span>
            </div>
            <span class="text-[9px] font-black text-slate-800 bg-white/95 px-1 rounded shadow-xs mt-0.5 whitespace-nowrap border border-slate-200">
              ${shortTownName}
            </span>
          </div>
        `;
        iconSize = [40, 36];
        iconAnchor = [20, 10];
      }

      const customIcon = L.divIcon({
        className: 'custom-substation-pin',
        html,
        iconSize,
        iconAnchor,
      });

      const marker = L.marker([sub.lat, sub.lng], { icon: customIcon, zIndexOffset: isSelected ? 100 : 10 });

      marker.on('click', () => {
        setActiveSubstation(sub);
        if (mapInstanceRef.current) {
          mapInstanceRef.current.flyTo([sub.lat, sub.lng], 12, { duration: 0.8 });
        }
      });

      // Tooltip popup
      marker.bindPopup(`
        <div style="font-family: sans-serif; font-size: 12px; line-height: 1.5; color: #0f172a;">
          <strong style="font-size: 13px; color: #0284c7;">${sub.name}</strong><br/>
          <span style="color: #64748b;">${sub.locationName}</span><br/>
          <hr style="margin: 4px 0; border: 0; border-top: 1px solid #e2e8f0;"/>
          <b>M.Tr 여유:</b> ${sub.remainingMw} MW<br/>
          <b>선로 잔여:</b> <span style="color: ${sub.status === 'SATURATED' ? '#e11d48' : '#059669'}; font-weight: bold;">
            ${sub.status === 'SATURATED' ? '0 kW (마감)' : sub.totalDlRemainKw.toLocaleString() + ' kW'}
          </span><br/>
          <b>관할:</b> ${sub.branchName}
        </div>
      `);

      marker.addTo(markersLayer);
    });
  }, [filteredSubstations, activeSubstation, currentZoom, viewMode]);

  // Quick Region & City Zoom Handlers
  const handleZoomRegion = (lat: number, lng: number, zoomLevel: number, regionKey: string) => {
    setSelectedRegion(regionKey);
    setSelectedCity('ALL');
    if (mapInstanceRef.current) {
      mapInstanceRef.current.flyTo([lat, lng], zoomLevel, { duration: 0.8 });
    }
  };

  const handleZoomCity = (city: string) => {
    setSelectedCity(city);
    const targetSub = NATIONWIDE_SUBSTATIONS.find((s) => s.city === city);
    if (targetSub && mapInstanceRef.current) {
      mapInstanceRef.current.flyTo([targetSub.lat, targetSub.lng], 11.5, { duration: 0.8 });
      setActiveSubstation(targetSub);
    }
  };

  const handleRefresh = () => {
    setIsRefreshing(true);
    setTimeout(() => {
      setIsRefreshing(false);
    }, 600);
  };

  // CSV Export for Sales Target extraction
  const handleExportCSV = () => {
    const headers = ['변전소명', '권역', '시군', '읍면', '행정구역', '관할지사', '위도(Lat)', '경도(Lng)', 'M.Tr잔여(MW)', '배전선로잔여(kW)', '상태', '영업등급', '관리자메모'];
    const rows = filteredSubstations.map((s) => [
      `"${s.name}"`,
      `"${s.region}"`,
      `"${s.city}"`,
      `"${s.town}"`,
      `"${s.locationName}"`,
      `"${s.branchName}"`,
      s.lat,
      s.lng,
      s.remainingMw,
      s.totalDlRemainKw,
      s.status === 'ABUNDANT' ? '여유풍부' : s.status === 'NORMAL' ? '보통' : '포화마감',
      s.salesGrade,
      `"${s.memo.replace(/"/g, '""')}"`,
    ]);

    const csvContent = '\uFEFF' + [headers.join(','), ...rows.map((r) => r.join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `한전_전국전수_배전선로_GIS정밀관제데이터_${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 font-sans selection:bg-sky-500 selection:text-white flex flex-col">
      
      {/* 1. Master Top Bar */}
      <header className="sticky top-0 z-40 bg-white/95 backdrop-blur-md border-b border-slate-200 py-3 px-4 sm:px-6 shadow-sm">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-3">
          
          <div className="flex items-center gap-2 w-full sm:w-auto justify-between sm:justify-start">
            {onBackToPortal ? (
              <button
                type="button"
                onClick={onBackToPortal}
                className="flex items-center gap-1.5 text-xs font-bold text-slate-700 hover:text-slate-900 bg-slate-100 hover:bg-slate-200 border border-slate-200 px-3 py-2 rounded-xl transition-all cursor-pointer shadow-sm"
              >
                <ChevronLeft className="w-4 h-4 text-sky-600" />
                <span>포털 메인으로</span>
              </button>
            ) : (
              <button
                type="button"
                onClick={onBackToHome}
                className="flex items-center gap-1.5 text-xs font-bold text-slate-700 hover:text-slate-900 bg-slate-100 hover:bg-slate-200 border border-slate-200 px-3 py-2 rounded-xl transition-all cursor-pointer shadow-sm"
              >
                <ChevronLeft className="w-4 h-4 text-sky-600" />
                <span>메인 홈으로</span>
              </button>
            )}

            {onBackToPortal && onBackToHome && (
              <button
                type="button"
                onClick={onBackToHome}
                className="text-xs font-bold text-slate-500 hover:text-slate-900 bg-white hover:bg-slate-100 border border-slate-200 px-3 py-2 rounded-xl transition-all cursor-pointer shadow-xs"
              >
                <span>메인 홈</span>
              </button>
            )}

            <div className="flex items-center gap-2">
              <div className="h-7 w-7 rounded-lg bg-slate-900 p-0.5 shadow flex items-center justify-center overflow-hidden shrink-0">
                <img src="/plus_sd_logo.png" alt="플러스에스디" className="w-full h-full object-contain" />
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm font-black text-slate-900 tracking-tight">
                  한전 계통 GIS 실시간 관제센터
                </span>
                <span className="px-2 py-0.5 rounded-full bg-indigo-50 border border-indigo-200 text-indigo-700 text-[10px] font-black uppercase tracking-wider">
                  4. GIS 관제 맵
                </span>
              </div>
            </div>
          </div>

          {/* Quick Subpage Switcher Tabs */}
          {onNavigateSubpage && (
            <div className="flex items-center gap-1.5 overflow-x-auto text-xs">
              <button
                onClick={() => onNavigateSubpage('admin-proposal')}
                className="px-3 py-1.5 rounded-xl bg-slate-100 hover:bg-slate-200 border border-slate-200 text-slate-600 font-bold cursor-pointer transition-colors"
              >
                1. 간단 견적서 만들기
              </button>
              <button
                onClick={() => onNavigateSubpage('admin-diagnostic')}
                className="px-3 py-1.5 rounded-xl bg-slate-100 hover:bg-slate-200 border border-slate-200 text-slate-600 font-bold cursor-pointer transition-colors"
              >
                2. 계통 여부 확인
              </button>
              <button
                onClick={() => onNavigateSubpage('admin-golden-targets')}
                className="px-3 py-1.5 rounded-xl bg-slate-100 hover:bg-slate-200 border border-slate-200 text-slate-600 font-bold cursor-pointer transition-colors"
              >
                3. 영업 추천 지역
              </button>
              <button
                onClick={() => onNavigateSubpage('admin-map')}
                className="px-3 py-1.5 rounded-xl bg-indigo-600 text-white font-bold cursor-pointer shadow-sm"
              >
                4. GIS 관제 맵
              </button>
            </div>
          )}

          {/* Top Quick Actions */}
          <div className="flex items-center gap-2 w-full sm:w-auto justify-end">
            <button
              onClick={handleRefresh}
              className="px-3 py-1.5 rounded-xl bg-slate-100 hover:bg-slate-200 border border-slate-200 text-slate-700 text-xs font-bold flex items-center gap-1.5 transition-colors cursor-pointer"
              title="실시간 전산 갱신"
            >
              <RefreshCw className={`w-3.5 h-3.5 text-sky-600 ${isRefreshing ? 'animate-spin' : ''}`} />
              <span className="hidden sm:inline">실시간 갱신</span>
            </button>

            <button
              onClick={handleExportCSV}
              className="px-3 py-1.5 rounded-xl bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 text-emerald-700 text-xs font-bold flex items-center gap-1.5 transition-colors cursor-pointer shadow-sm"
            >
              <Download className="w-3.5 h-3.5" />
              <span>전국 전수 DB (CSV)</span>
            </button>

            <a
              href="https://online.kepco.co.kr"
              target="_blank"
              rel="noopener noreferrer"
              className="px-3 py-1.5 rounded-xl bg-sky-50 hover:bg-sky-100 border border-sky-200 text-sky-700 text-xs font-bold flex items-center gap-1 transition-colors"
            >
              <span>한전 ON</span>
              <ExternalLink className="w-3 h-3" />
            </a>
          </div>

        </div>
      </header>

      {/* 2. KPI Summary Bar */}
      <section className="bg-white border-b border-slate-200 px-4 sm:px-6 py-3">
        <div className="max-w-7xl mx-auto grid grid-cols-2 sm:grid-cols-6 gap-2 sm:gap-3 text-xs">
          
          <div className="bg-slate-50 p-3 rounded-2xl border border-slate-200/80">
            <span className="text-slate-500 block text-[11px] mb-0.5 font-bold">관제 읍·면 거점 총계</span>
            <strong className="text-base font-black text-slate-900">{metrics.total}개소</strong>
          </div>

          <div className="bg-emerald-50/70 p-3 rounded-2xl border border-emerald-200">
            <span className="text-emerald-700 block text-[11px] font-bold mb-0.5 flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
              여유 풍부 (3MW+)
            </span>
            <strong className="text-base font-black text-emerald-700">{metrics.abundant}개소</strong>
          </div>

          <div className="bg-amber-50/70 p-3 rounded-2xl border border-amber-200">
            <span className="text-amber-800 block text-[11px] font-bold mb-0.5 flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-amber-500" />
              보통 (500kW~3MW)
            </span>
            <strong className="text-base font-black text-amber-800">{metrics.normal}개소</strong>
          </div>

          <div className="bg-rose-50/70 p-3 rounded-2xl border border-rose-200">
            <span className="text-rose-700 block text-[11px] font-bold mb-0.5 flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-rose-500" />
              포화/마감 (0 kW)
            </span>
            <strong className="text-base font-black text-rose-700">{metrics.saturated}개소 (선점필수)</strong>
          </div>

          <div className="bg-sky-50/70 p-3 rounded-2xl border border-sky-200">
            <span className="text-sky-700 block text-[11px] font-bold mb-0.5">총 잔여 연계용량</span>
            <strong className="text-base font-black text-sky-700">
              {(metrics.totalRemainingKw / 1000).toFixed(1)} MW
            </strong>
          </div>

          <div className="bg-indigo-50/70 p-3 rounded-2xl border border-indigo-200">
            <span className="text-indigo-700 block text-[11px] font-bold mb-0.5">타깃 산단 지붕면적</span>
            <strong className="text-base font-black text-indigo-700">
              {(metrics.totalRoofPyung / 10000).toFixed(0)}만 평
            </strong>
          </div>

        </div>
      </section>

      {/* 3. Multi-Tier Hierarchical Filter & Granular Chips */}
      <section className="bg-slate-50 border-b border-slate-200 px-4 sm:px-6 py-3.5 space-y-3">
        <div className="max-w-7xl mx-auto flex flex-col lg:flex-row items-stretch lg:items-center justify-between gap-3">
          
          {/* Status Filter Chips */}
          <div className="flex flex-wrap items-center gap-1.5 text-xs">
            <span className="text-slate-600 font-bold mr-1 flex items-center gap-1 text-[11px]">
              <Filter className="w-3.5 h-3.5 text-sky-600" />
              용량상태:
            </span>
            {[
              { key: 'ALL', label: '전체보기' },
              { key: 'ABUNDANT', label: '🟢 여유 풍부 (3MW+)' },
              { key: 'NORMAL', label: '🟡 보통' },
              { key: 'SATURATED', label: '🔴 포화 마감 (0kW)' },
            ].map((st) => (
              <button
                key={st.key}
                type="button"
                onClick={() => setSelectedStatus(st.key)}
                className={`px-3 py-1.5 rounded-xl font-extrabold transition-all cursor-pointer text-xs ${
                  selectedStatus === st.key
                    ? 'bg-sky-600 text-white shadow-sm'
                    : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-100'
                }`}
              >
                {st.label}
              </button>
            ))}
          </div>

          {/* Region & City Granular Selectors + Search Box */}
          <div className="flex flex-wrap items-center gap-2">
            
            {/* 1. 광역 권역 Selector */}
            <div className="flex items-center gap-1 text-xs">
              <span className="text-slate-600 font-bold text-[11px]">권역:</span>
              <select
                value={selectedRegion}
                onChange={(e) => {
                  const val = e.target.value;
                  if (val === 'ALL') handleZoomRegion(36.6, 127.8, 7.5, 'ALL');
                  else if (val === '서울특별시') handleZoomRegion(37.51, 126.98, 11.5, '서울특별시');
                  else if (val === '경기남부') handleZoomRegion(37.15, 127.0, 10, '경기남부');
                  else if (val === '경기북부') handleZoomRegion(37.85, 127.0, 10, '경기북부');
                  else if (val === '강원') handleZoomRegion(37.6, 128.2, 9, '강원');
                  else if (val === '충청') handleZoomRegion(36.8, 127.3, 9, '충청');
                  else if (val === '호남') handleZoomRegion(35.4, 126.9, 9, '호남');
                  else if (val === '영남') handleZoomRegion(35.8, 128.6, 9, '영남');
                }}
                className="bg-white border border-slate-200 text-slate-800 text-xs px-3 py-1.5 rounded-xl focus:border-sky-500 focus:outline-none cursor-pointer font-bold shadow-xs"
              >
                <option value="ALL">전국 권역 (전체)</option>
                <option value="서울특별시">서울특별시 (금천 G밸리, 송파 물류, 성수, 마곡, 서초)</option>
                <option value="경기남부">경기 남부 (화성, 평택, 안성, 이천, 안산, 시흥, 용인)</option>
                <option value="경기북부">경기 북부 (포천, 파주, 양주, 김포)</option>
                <option value="강원">강원권 (춘천, 원주, 강릉, 횡성)</option>
                <option value="충청">충청권 (당진, 천안, 아산, 진천, 음성, 충주)</option>
                <option value="호남">호남권 (나주, 익산, 군산)</option>
                <option value="영남">영남권 (구미, 김해, 창원)</option>
              </select>
            </div>

            {/* 2. 세부 시/군 Granular Selector */}
            <div className="flex items-center gap-1 text-xs">
              <span className="text-slate-600 font-bold text-[11px]">시·군:</span>
              <select
                value={selectedCity}
                onChange={(e) => {
                  const cityVal = e.target.value;
                  if (cityVal === 'ALL') {
                    setSelectedCity('ALL');
                  } else {
                    handleZoomCity(cityVal);
                  }
                }}
                className="bg-white border border-slate-200 text-sky-700 font-bold text-xs px-3 py-1.5 rounded-xl focus:border-sky-500 focus:outline-none cursor-pointer shadow-xs"
              >
                <option value="ALL">시·군 전체 ({availableCities.length}개)</option>
                {availableCities.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </div>

            {/* Search Input (Supports Jibeon / Town / Feeder / Substation Search) */}
            <div className="relative min-w-[180px] flex-1 sm:flex-initial">
              <input
                type="text"
                placeholder="지번, 읍/면, 선로, 산단명 검색..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-8 pr-3 py-1.5 rounded-xl bg-white border border-slate-200 text-slate-900 placeholder-slate-400 text-xs focus:border-sky-500 focus:outline-none shadow-xs"
              />
              <Search className="w-3.5 h-3.5 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  <X className="w-3 h-3" />
                </button>
              )}
            </div>

            {/* View Mode Toggle Button */}
            <div className="flex bg-slate-100 p-0.5 rounded-xl border border-slate-200 text-xs">
              <button
                type="button"
                onClick={() => setViewMode('map')}
                className={`px-2.5 py-1.5 rounded-lg font-bold flex items-center gap-1 cursor-pointer transition-all ${
                  viewMode === 'map' ? 'bg-sky-600 text-white shadow-xs' : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                <Layers className="w-3.5 h-3.5" />
                <span>GIS 지도</span>
              </button>
              <button
                type="button"
                onClick={() => setViewMode('table')}
                className={`px-2.5 py-1.5 rounded-lg font-bold flex items-center gap-1 cursor-pointer transition-all ${
                  viewMode === 'table' ? 'bg-sky-600 text-white shadow-xs' : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                <TableIcon className="w-3.5 h-3.5" />
                <span>리스트</span>
              </button>
            </div>

          </div>

        </div>
      </section>

      {/* 4. Main Body: Real Leaflet GIS Map or Data Table & Details Panel */}
      <main className="max-w-7xl mx-auto p-4 sm:p-6 w-full flex-1 flex flex-col lg:flex-row gap-5">
        
        {/* Left / Center Area: Real Interactive Leaflet GIS Map or Table */}
        <div className="flex-1 flex flex-col space-y-4">
          
          {viewMode === 'map' ? (
            /* ===================== REAL LEAFLET GIS MAP VIEW ===================== */
            <div className="relative w-full h-[620px] sm:h-[760px] bg-slate-100 rounded-3xl border border-slate-200 overflow-hidden shadow-sm">
              
              {/* Actual Leaflet Map Canvas Container */}
              <div
                ref={mapContainerRef}
                className="w-full h-full min-h-[620px] sm:min-h-[760px] z-0"
                style={{ width: '100%', height: '100%', minHeight: '620px' }}
              />

              {/* Map Layer Switcher & GIS Overlays Toggle (Top Right on Map) */}
              <div className="absolute top-4 right-4 z-20 flex flex-col sm:flex-row gap-2">
                
                {/* 1. Base Tile Switcher */}
                <div className="flex bg-white/95 backdrop-blur-md p-1 rounded-2xl border border-slate-200 text-xs shadow-md">
                  <button
                    type="button"
                    onClick={() => setTileMode('street')}
                    className={`px-2.5 py-1.5 rounded-xl font-bold flex items-center gap-1 cursor-pointer transition-all ${
                      tileMode === 'street' ? 'bg-sky-600 text-white shadow-xs' : 'text-slate-600 hover:text-slate-900'
                    }`}
                  >
                    <MapIcon className="w-3.5 h-3.5" />
                    <span>일반</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => setTileMode('satellite')}
                    className={`px-2.5 py-1.5 rounded-xl font-bold flex items-center gap-1 cursor-pointer transition-all ${
                      tileMode === 'satellite' ? 'bg-sky-600 text-white shadow-xs' : 'text-slate-600 hover:text-slate-900'
                    }`}
                  >
                    <Layers className="w-3.5 h-3.5" />
                    <span>위성</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => setTileMode('dark')}
                    className={`px-2.5 py-1.5 rounded-xl font-bold flex items-center gap-1 cursor-pointer transition-all ${
                      tileMode === 'dark' ? 'bg-sky-600 text-white shadow-xs' : 'text-slate-600 hover:text-slate-900'
                    }`}
                  >
                    <span>다크</span>
                  </button>
                </div>

                {/* 2. GIS Feature Layers Toggle (Powerlines / Industrial Parks) */}
                <div className="flex bg-white/95 backdrop-blur-md p-1 rounded-2xl border border-slate-200 text-xs shadow-md">
                  <button
                    type="button"
                    onClick={() => setShowPowerlines(!showPowerlines)}
                    className={`px-2.5 py-1.5 rounded-xl font-bold flex items-center gap-1 cursor-pointer transition-all ${
                      showPowerlines ? 'bg-emerald-600 text-white shadow-xs' : 'bg-slate-100 text-slate-500'
                    }`}
                    title="22.9kV 배전선로 케이블 궤적 표시"
                  >
                    <Activity className="w-3.5 h-3.5" />
                    <span>⚡ 배전선로망</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => setShowIndustrialParks(!showIndustrialParks)}
                    className={`px-2.5 py-1.5 rounded-xl font-bold flex items-center gap-1 cursor-pointer transition-all ${
                      showIndustrialParks ? 'bg-amber-500 text-slate-950 font-black shadow-xs' : 'bg-slate-100 text-slate-500'
                    }`}
                    title="공장 및 산업단지 지붕 타깃 표시"
                  >
                    <Building2 className="w-3.5 h-3.5" />
                    <span>🏢 산단·공장</span>
                  </button>
                </div>

              </div>

              {/* Map Quick Region Jump Bar (Bottom Left on Map) */}
              <div className="absolute bottom-4 left-4 z-20 flex flex-wrap items-center gap-1 bg-white/95 backdrop-blur-md p-1.5 rounded-2xl border border-slate-200 shadow-md max-w-xl">
                <span className="text-[11px] font-extrabold text-slate-600 px-2 flex items-center gap-1">
                  <Maximize2 className="w-3 h-3 text-sky-600" />
                  <span>핵심 권역:</span>
                </span>
                <button
                  onClick={() => handleZoomRegion(36.6, 127.8, 7.5, 'ALL')}
                  className="px-2 py-1 rounded-lg bg-slate-100 hover:bg-slate-200 text-[11px] font-bold text-slate-700"
                >
                  전국
                </button>
                <button
                  onClick={() => handleZoomRegion(37.51, 126.98, 11.5, '서울특별시')}
                  className="px-2 py-1 rounded-lg bg-slate-100 hover:bg-slate-200 text-[11px] font-bold text-sky-700"
                >
                  서울 (G밸리/물류단지)
                </button>
                <button
                  onClick={() => handleZoomCity('화성시')}
                  className="px-2 py-1 rounded-lg bg-slate-100 hover:bg-slate-200 text-[11px] font-bold text-sky-700"
                >
                  화성시 (팔탄/향남/마도)
                </button>
                <button
                  onClick={() => handleZoomCity('평택시')}
                  className="px-2 py-1 rounded-lg bg-slate-100 hover:bg-slate-200 text-[11px] font-bold text-sky-700"
                >
                  평택시 (포승/고덕)
                </button>
                <button
                  onClick={() => handleZoomCity('포천시')}
                  className="px-2 py-1 rounded-lg bg-slate-100 hover:bg-slate-200 text-[11px] font-bold text-emerald-700"
                >
                  포천시 (군내/가산)
                </button>
                <button
                  onClick={() => handleZoomCity('원주시')}
                  className="px-2 py-1 rounded-lg bg-slate-100 hover:bg-slate-200 text-[11px] font-bold text-emerald-700"
                >
                  원주시 (문막산단)
                </button>
                <button
                  onClick={() => handleZoomCity('당진시')}
                  className="px-2 py-1 rounded-lg bg-slate-100 hover:bg-slate-200 text-[11px] font-bold text-amber-800"
                >
                  당진시 (송악/석문)
                </button>
                <button
                  onClick={() => handleZoomCity('진천군')}
                  className="px-2 py-1 rounded-lg bg-slate-100 hover:bg-slate-200 text-[11px] font-bold text-amber-800"
                >
                  진천/음성
                </button>
                <button
                  onClick={() => handleZoomCity('구미시')}
                  className="px-2 py-1 rounded-lg bg-slate-100 hover:bg-slate-200 text-[11px] font-bold text-sky-700"
                >
                  구미시 (4·5단지)
                </button>
              </div>

              {/* Collapsible Floating Map Legend (Top Left on Map) */}
              <div className="absolute top-4 left-4 z-20 bg-white/95 backdrop-blur-md border border-slate-200 rounded-2xl shadow-md text-xs overflow-hidden max-w-[190px]">
                <button
                  type="button"
                  onClick={() => setLegendOpen(!legendOpen)}
                  className="w-full px-3 py-2 text-left font-extrabold text-slate-900 text-[11px] flex items-center justify-between hover:bg-slate-50 cursor-pointer"
                >
                  <span>한전 계통 범례</span>
                  {legendOpen ? <ChevronUp className="w-3.5 h-3.5 text-slate-500" /> : <ChevronDown className="w-3.5 h-3.5 text-slate-500" />}
                </button>

                {legendOpen && (
                  <div className="p-3 pt-1 space-y-1 text-[11px] border-t border-slate-100 animate-in fade-in">
                    <div className="flex items-center gap-2">
                      <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 shadow-xs" />
                      <span className="text-slate-700">여유 풍부 (3MW+)</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="w-2.5 h-2.5 rounded-full bg-amber-500 shadow-xs" />
                      <span className="text-slate-700">보통 (500kW~3MW)</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="w-2.5 h-2.5 rounded-full bg-rose-500 shadow-xs" />
                      <span className="text-slate-700">포화 마감 (0kW)</span>
                    </div>
                    <div className="flex items-center gap-2 pt-1 border-t border-slate-100 text-[10px]">
                      <span className="w-3 h-0.5 bg-emerald-500 rounded" />
                      <span className="text-emerald-700">여유 선로망</span>
                      <span className="w-3 h-0.5 bg-rose-500 rounded ml-1" />
                      <span className="text-rose-700">포화 선로</span>
                    </div>
                    <div className="flex items-center gap-1.5 text-[10px] text-amber-700 font-bold">
                      <span>🏢</span>
                      <span>타깃 공장/산단</span>
                    </div>
                  </div>
                )}
              </div>

            </div>
          ) : (
            /* ===================== TABLE VIEW ===================== */
            <div className="bg-white border border-slate-200 rounded-3xl overflow-hidden shadow-sm">
              <div className="overflow-x-auto">
                <table className="w-full text-left text-xs">
                  <thead className="bg-slate-50 border-b border-slate-200 text-slate-600 font-extrabold uppercase text-[11px]">
                    <tr>
                      <th className="p-3.5">변전소명</th>
                      <th className="p-3.5">시·군 / 읍·면</th>
                      <th className="p-3.5">상세 행정구역</th>
                      <th className="p-3.5">관할 한전 지사</th>
                      <th className="p-3.5">M.Tr 잔여</th>
                      <th className="p-3.5">D/L 선로 잔여</th>
                      <th className="p-3.5">계통 상태</th>
                      <th className="p-3.5">영업 등급</th>
                      <th className="p-3.5 text-center">선택</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 text-slate-700">
                    {filteredSubstations.map((sub) => {
                      const isSelected = activeSubstation?.id === sub.id;
                      return (
                        <tr
                          key={sub.id}
                          onClick={() => setActiveSubstation(sub)}
                          className={`hover:bg-slate-50 cursor-pointer transition-colors ${
                            isSelected ? 'bg-sky-50' : ''
                          }`}
                        >
                          <td className="p-3.5 font-black text-slate-900 flex items-center gap-2">
                            <span className={`w-2 h-2 rounded-full shrink-0 ${
                              sub.status === 'SATURATED' ? 'bg-rose-500' : 'bg-emerald-500'
                            }`} />
                            <span>{sub.name}</span>
                          </td>
                          <td className="p-3.5">
                            <span className="font-bold text-sky-700 mr-1.5">{sub.city}</span>
                            <span className="text-slate-800 font-medium">{sub.town}</span>
                          </td>
                          <td className="p-3.5 text-slate-600">{sub.locationName}</td>
                          <td className="p-3.5 text-slate-600">{sub.branchName}</td>
                          <td className="p-3.5 font-bold text-slate-900">{sub.remainingMw} MW</td>
                          <td className="p-3.5">
                            <strong className={sub.status === 'SATURATED' ? 'text-rose-600' : 'text-emerald-600 font-black'}>
                              {sub.status === 'SATURATED' ? '0 kW' : `${sub.totalDlRemainKw.toLocaleString()} kW`}
                            </strong>
                          </td>
                          <td className="p-3.5">
                            <span className={`px-2 py-0.5 rounded-full text-[10px] font-extrabold border ${
                              sub.status === 'SATURATED'
                                ? 'bg-rose-50 border-rose-200 text-rose-600'
                                : 'bg-emerald-50 border-emerald-200 text-emerald-700'
                            }`}>
                              {sub.status === 'SATURATED' ? '0kW 포화마감' : '인입 여유'}
                            </span>
                          </td>
                          <td className="p-3.5">
                            <span className={`font-black px-2 py-0.5 rounded text-xs ${
                              sub.salesGrade === 'S'
                                ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                                : sub.salesGrade === 'A'
                                ? 'bg-sky-50 text-sky-700 border border-sky-200'
                                : 'bg-slate-100 text-slate-600'
                            }`}>
                              {sub.salesGrade}등급
                            </span>
                          </td>
                          <td className="p-3.5 text-center">
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                setActiveSubstation(sub);
                              }}
                              className="px-2.5 py-1 rounded-lg bg-sky-600 hover:bg-sky-500 text-white font-bold text-[11px]"
                            >
                              상세보기
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

        </div>

        {/* Right Area: Selected Substation & Industrial Park & Line Inspector Drawer */}
        <div className="w-full lg:w-96 flex flex-col space-y-4">
          
          {/* 1. Active Industrial Park Target Block (If selected) */}
          {activePark && (
            <div className="bg-amber-50 border border-amber-200 rounded-3xl p-5 shadow-sm space-y-3">
              <div className="flex items-center justify-between">
                <span className="px-2.5 py-0.5 rounded-full bg-amber-100 text-amber-800 text-[10px] font-black uppercase">
                  🏢 {activePark.targetType} 타깃
                </span>
                <button
                  onClick={() => setActivePark(null)}
                  className="text-slate-400 hover:text-slate-600 cursor-pointer"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              <h3 className="text-base font-black text-slate-900">{activePark.name}</h3>
              <p className="text-xs text-slate-600">{activePark.city} {activePark.town} 일대</p>

              <div className="grid grid-cols-2 gap-2 text-xs pt-1">
                <div className="bg-white p-2.5 rounded-xl border border-amber-200/80">
                  <span className="text-slate-500 block text-[10px] font-bold">지붕 추정 면적</span>
                  <strong className="text-amber-700 font-black">{activePark.estimatedRoofPyung.toLocaleString()} 평</strong>
                </div>
                <div className="bg-white p-2.5 rounded-xl border border-amber-200/80">
                  <span className="text-slate-500 block text-[10px] font-bold">잠재 발전 용량</span>
                  <strong className="text-sky-700 font-black">{(activePark.potentialKw / 1000).toFixed(1)} MW</strong>
                </div>
              </div>

              <div className="bg-white p-2.5 rounded-xl border border-amber-200/80 text-xs flex items-center justify-between">
                <span className="text-slate-600 font-bold">연계 배전선로</span>
                <strong className={activePark.status === 'SATURATED' ? 'text-rose-600' : 'text-emerald-700'}>
                  ⚡ {activePark.connectedFeeder} ({activePark.status === 'SATURATED' ? '0kW 포화' : '여유확보'})
                </strong>
              </div>
            </div>
          )}

          {/* 2. Active Feeder Line (If selected) */}
          {activeLine && (
            <div className="bg-sky-50 border border-sky-200 rounded-3xl p-5 shadow-sm space-y-3">
              <div className="flex items-center justify-between">
                <span className="px-2.5 py-0.5 rounded-full bg-sky-100 text-sky-800 text-[10px] font-black uppercase">
                  ⚡ 22.9kV 배전선로 궤적
                </span>
                <button
                  onClick={() => setActiveLine(null)}
                  className="text-slate-400 hover:text-slate-600 cursor-pointer"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              <h3 className="text-base font-black text-slate-900">{activeLine.feederName}</h3>
              <p className="text-xs text-slate-600">{activeLine.substationName} 소속 선로</p>

              <div className="grid grid-cols-2 gap-2 text-xs pt-1">
                <div className="bg-white p-2.5 rounded-xl border border-sky-200/80">
                  <span className="text-slate-500 block text-[10px] font-bold">선로 허용 용량</span>
                  <strong className="text-slate-800 font-black">{activeLine.capacityKw.toLocaleString()} kW</strong>
                </div>
                <div className="bg-white p-2.5 rounded-xl border border-sky-200/80">
                  <span className="text-slate-500 block text-[10px] font-bold">잔여 송전 용량</span>
                  <strong className={activeLine.status === 'SATURATED' ? 'text-rose-600 font-black' : 'text-emerald-700 font-black'}>
                    {activeLine.status === 'SATURATED' ? '0 kW (마감)' : `${activeLine.remainingKw.toLocaleString()} kW`}
                  </strong>
                </div>
              </div>
            </div>
          )}

          {/* 3. Substation Main Inspector */}
          {activeSubstation ? (
            <div className="bg-white border border-slate-200/90 rounded-3xl p-5 sm:p-6 shadow-sm space-y-5 sticky top-24">
              
              {/* Header */}
              <div className="border-b border-slate-100 pb-4">
                <div className="flex items-center justify-between mb-2">
                  <span className={`px-2.5 py-0.5 rounded-full text-xs font-black border ${
                    activeSubstation.status === 'SATURATED'
                      ? 'bg-rose-50 border-rose-200 text-rose-700'
                      : 'bg-emerald-50 border-emerald-200 text-emerald-700'
                  }`}>
                    {activeSubstation.status === 'SATURATED' ? '🔴 송전선로 포화 마감' : '🟢 인입 즉시 착공 권역'}
                  </span>
                  
                  <span className="text-xs font-black text-sky-700 bg-sky-50 px-2 py-0.5 rounded border border-sky-200">
                    영업 {activeSubstation.salesGrade}등급
                  </span>
                </div>

                <div className="flex items-center gap-1.5 text-xs text-sky-600 font-extrabold mb-1">
                  <span>{activeSubstation.region}</span>
                  <span>&gt;</span>
                  <span>{activeSubstation.city}</span>
                  <span>&gt;</span>
                  <span>{activeSubstation.town}</span>
                </div>

                <h2 className="text-xl font-black text-slate-900 tracking-tight">
                  {activeSubstation.name}
                </h2>
                <p className="text-xs text-slate-500 font-medium mt-0.5 flex items-center gap-1">
                  <MapPin className="w-3.5 h-3.5 text-slate-400" />
                  <span>{activeSubstation.locationName}</span>
                </p>
              </div>

              {/* Capacity Specs */}
              <div className="space-y-2 text-xs">
                <div className="bg-slate-50 p-3 rounded-xl border border-slate-200/80 flex items-center justify-between">
                  <span className="text-slate-600 font-bold">한전 관할 지사</span>
                  <strong className="text-slate-900 font-bold">{activeSubstation.branchName}</strong>
                </div>

                <div className="bg-slate-50 p-3 rounded-xl border border-slate-200/80 flex items-center justify-between">
                  <span className="text-slate-600 font-bold">변전소 M.Tr 여유</span>
                  <strong className={`font-black ${
                    activeSubstation.status === 'SATURATED' ? 'text-rose-600' : 'text-emerald-700'
                  }`}>
                    {activeSubstation.remainingMw} MW / {activeSubstation.totalCapacityMw} MW
                  </strong>
                </div>

                <div className="bg-slate-50 p-3 rounded-xl border border-slate-200/80 flex items-center justify-between">
                  <span className="text-slate-600 font-bold">소속 배전선로(D/L) 총 잔여</span>
                  <strong className={`font-black text-sm ${
                    activeSubstation.status === 'SATURATED' ? 'text-rose-600' : 'text-sky-700'
                  }`}>
                    {activeSubstation.totalDlRemainKw.toLocaleString()} kW
                  </strong>
                </div>
              </div>

              {/* D/L Lines Breakdown List */}
              <div className="space-y-2">
                <span className="text-xs font-black text-slate-800 block tracking-tight">
                  소속 22.9kV 배전선로 (D/L) 현황 ({activeSubstation.dlList.length}회선)
                </span>

                <div className="space-y-1.5 max-h-48 overflow-y-auto pr-1">
                  {activeSubstation.dlList.map((dl, idx) => (
                    <div
                      key={idx}
                      className="bg-slate-50 p-2.5 rounded-xl border border-slate-200 flex items-center justify-between text-xs"
                    >
                      <div>
                        <strong className="text-slate-900 font-bold block">{dl.name}</strong>
                        <span className="text-[10px] text-slate-500">{dl.voltage}</span>
                      </div>
                      <div className="text-right">
                        <strong className={`block font-black ${dl.isSaturated ? 'text-rose-600' : 'text-emerald-700'}`}>
                          {dl.isSaturated ? '0 kW (마감)' : `${dl.remainingKw.toLocaleString()} kW`}
                        </strong>
                        <span className="text-[10px] text-slate-500">허용 {dl.capacityKw.toLocaleString()}kW</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Admin Strategy Memo */}
              <div className="bg-sky-50 border border-sky-200 p-3.5 rounded-2xl text-xs space-y-1.5 text-slate-700">
                <span className="text-sky-800 font-black flex items-center gap-1.5">
                  <Cpu className="w-4 h-4" />
                  <span>관리자 영업 전략 가이드</span>
                </span>
                <p className="text-[11px] leading-relaxed text-slate-700 font-medium keep-all">
                  {activeSubstation.memo}
                </p>
                {activeSubstation.expansionPlan && (
                  <p className="text-[11px] text-amber-800 font-bold mt-1">
                    📌 {activeSubstation.expansionPlan}
                  </p>
                )}
              </div>

              {/* Direct KEPCO Action Button */}
              <a
                href="https://online.kepco.co.kr"
                target="_blank"
                rel="noopener noreferrer"
                className="w-full py-3.5 rounded-2xl bg-sky-600 hover:bg-sky-500 text-white font-black text-xs transition-all flex items-center justify-center gap-2 shadow-md shadow-sky-600/20 active:scale-95"
              >
                <span>한전 ON 실시간 전산 대조</span>
                <ExternalLink className="w-3.5 h-3.5" />
              </a>

            </div>
          ) : (
            <div className="bg-white border border-slate-200 rounded-3xl p-8 text-center text-slate-500 text-xs shadow-sm">
              지도에서 변전소 핀 또는 선로를 선택해 주세요.
            </div>
          )}
        </div>

      </main>

    </div>
  );
};
